import { useEffect, useRef } from 'react'
import { defaults } from 'lodash'

import { Logger, PubNub } from '@shared/utils'

const log = Logger('usePubNub')

/**
 * Helper hook to reduce boilerplate when subscribing to PubNub events.
 *
 * @example
 * usePubNub(
 *   'some-event,
 *   (data) => console.log(data)
 *   { enabled: Boolean(conversationId) }
 * )
 */
export default function usePubNub(channel, listener, options = {}) {
  const subscriber = useRef(listener)

  const { enabled } = defaults(options, {
    enabled: true,
  })

  useEffect(() => {
    subscriber.current = listener
  }, [listener])

  useEffect(() => {
    if (!enabled) return

    const id = PubNub.on(channel, subscriber.current)
    log.debug('[SUBSCRIBED]', id, channel)
    return () => {
      PubNub.off(id, channel)
      log.debug('[UNSUBSCRIBED]', id, channel)
    }
  }, [channel, enabled])
}

export const Channels = {
  PatientJoined: 'patient_joined',
  JoinAppointment: 'join_appointment',
  ProviderJoined: 'provider_joined',
  VideoCompleted: 'completed_video',
  NewMessage: 'new_message',
  MentionMessage: 'message_mention',
  UnreviewedLabs: 'unreviewed_labs',
  CallStatusChanged: 'call_status_changed',
  UpdateQuarterlyFlowCount: 'update_quarterly_flow_count',
  ReassignedPatient: 'reassigned_patient',
  AppointmentStatusChanged: 'status_changed',
  EmailVerified: 'email_verified',
  Notify: 'notify',
  ScheduleBoard: 'schedule_board',
  // On Demands
  OnDemandPending: 'on_demand_appointment_pending',
  OnDemandAccepted: 'on_demand_appointment_accepted',
  OnDemandAbandoned: 'on_demand_appointment_abandoned',
  OnDemandRejected: 'on_demand_appointment_rejected',
  OnDemandStatusUpdate: 'on_demand_appointment_status_update',
  // Async Messaging Appointments
  AsyncMessagingWaiting: 'async_messaging_appointment_waiting',
  AsyncMessagingAccepted: 'async_messaging_appointment_accepted',
}
